import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { SessionActions } from '../../account/sessionSlice';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Avatar from '../../components/Avatar';
import { Field, FieldSet } from '../../components/Form';
import { gql } from '../../graphql/generated';
import UserProfileAvatarEditor from '../../user/settings/UserProfileAvatarEditor';
import { deltaToText, textToDelta } from '../../utils/text/deltaText';
import type { OrganizationOnboardingStepBaseProps } from './OrganizationOnboardingStep';
import OrganizationOnboardingStep from './OrganizationOnboardingStep';
import * as styles from './OrganizationOnboardingStepProfile.module.scss';

type Values = { name: string; title: string; description: string };

gql(`
  fragment OrganizationOnboardingStepProfile_user on User {
    id
    name
    title
    description
    ...UserProfileAvatarEditor_user
  }
`);

const USER_QUERY = gql(/** @complexity 2 */ `
  query OrganizationOnboardingStepProfileUser {
    session: currentSession {
      user {
        id
        ...OrganizationOnboardingStepProfile_user
      }
    }
  }
`);

const UPDATE_USER_PROFILE_MUTATION = gql(/** @complexity 2 */ `
  mutation OrganizationOnboardingStepProfile_updateUserProfile(
    $id: ID!
    $avatar: Upload
    $name: String
    $title: String
    $description: Json
  ) {
    updateUser(
      id: $id
      avatar: $avatar
      name: $name
      title: $title
      description: $description
    ) {
      id
      ...OrganizationOnboardingStepProfile_user
    }
  }
`);

const OrganizationOnboardingStepProfile = (
  props: OrganizationOnboardingStepBaseProps,
) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE_MUTATION);
  const { data } = useQuery(USER_QUERY);
  const user = data?.session.user;
  const isDisabled = !user;

  return (
    <OrganizationOnboardingStep<Values>
      {...props}
      title="Customize your profile"
      description="Enter your personal details"
      initialValues={{
        title: user?.title ?? '',
        name: user?.name ?? '',
        description: deltaToText(user?.description) ?? '',
      }}
      disabled={isDisabled}
      key={user?.id}
      onSubmit={async ({ name, title, description }) => {
        if (!user) return;

        const result = await updateUserProfile({
          variables: {
            id: user.id,
            name,
            title,
            description: JSON.stringify(textToDelta(description)),
          },
        });

        if (result.data?.updateUser) {
          dispatch(
            SessionActions.setCurrentUser({
              ...currentUser,
              ...result.data.updateUser,
            }),
          );
        }
      }}
    >
      <div className={styles.avatarEditorContainer}>
        <h5 className={styles.label}>Picture</h5>
        {!isDisabled ? (
          <UserProfileAvatarEditor size="medium" user={user} />
        ) : (
          <Avatar skeleton size="medium" />
        )}
      </div>
      <FieldSet disabled={isDisabled} margin="none">
        <Field name="name" label="Name" />
        <Field name="title" label="Title" />
        <Field
          name="description"
          label="Description"
          type="textarea"
          margin="none"
        />
      </FieldSet>
    </OrganizationOnboardingStep>
  );
};

export default OrganizationOnboardingStepProfile;
